import { PrimaryButton, SecondaryButton } from '@curie/react'
import { Popover, PopoverContent, PopoverTrigger, useDisclosure } from '@nextui-org/react'
import { VscCode } from '@react-icons/all-files/vsc/VscCode'
import { t } from 'i18next'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useConversationUuid, useReviewDiffCommentsWithRef } from '~/hooks'
import { I18nKey } from '~/i18n/declaration'
import { Diff } from '~/protos/agents'
import { DiffComment } from '~/store/editorSlice'
import { AppEvents, globalEventEmitter } from '~/utils/shared'

import * as monaco from 'monaco-editor'
import { SimpleTextarea } from '~/components/SimpleTextarea'
import { getMonacoOptions, initializeMonaco } from './monacoInitializer'

export interface DiffEditorProps {
  diff: Diff | null
  onContentChange: (file: { filePath: string; fileContent: string }) => void
  readOnly?: boolean
}

let codelensDisposable: monaco.IDisposable | null = null
await initializeMonaco()

const DiffEditorComponent: React.FC<DiffEditorProps> = ({ diff, onContentChange, readOnly }) => {
  const isCodelensRegistered = useRef<boolean>(false)
  const cursorLocatorRef = useRef<HTMLDivElement | null>(null)
  const commentModalPayloadRef = useRef<Omit<DiffComment, 'comment'> | null>(null)
  const diffEditorRef = useRef<monaco.editor.IStandaloneDiffEditor | null>(null)
  const diffEditorElementRef = useRef<HTMLDivElement | null>(null)
  const monacoOptions = getMonacoOptions(readOnly)

  const {
    isOpen: isReviewModalOpen,
    onOpen: onReviewModalOpen,
    onClose: onReviewModalClose,
    onOpenChange: onReviewModalOpenChange
  } = useDisclosure()

  const handleResize = useCallback(() => {
    if (diffEditorRef.current) {
      diffEditorRef.current.layout()
    }
  }, [])

  const [comments, upsertComment, commentsRef] = useReviewDiffCommentsWithRef(diff?.bFilePath)

  useEffect(() => {
    if (diff != null) {
      const diffEditorElement = diffEditorElementRef.current!
      const containerElement = diffEditorElement.firstChild as HTMLDivElement
      const original = monaco.editor.createModel(diff!.aFileContent, 'xml')
      const modified = monaco.editor.createModel(diff!.bFileContent, 'xml')
      const diffEditor = monaco.editor.createDiffEditor(containerElement, monacoOptions)
      diffEditorRef.current = diffEditor

      const language = diff.bFilePath.split('.').pop() ?? 'xml'
      monaco.editor.setModelLanguage(original, language)
      monaco.editor.setModelLanguage(modified, language)

      diffEditor.setModel({ original, modified })
      const resizeObserver = new ResizeObserver(() => {
        diffEditor.layout()
      })
      resizeObserver.observe(diffEditorElement)
      window.addEventListener('resize', handleResize)

      return () => {
        // resizeObserver.disconnect()
        // window.removeEventListener('resize', handleResize)
        // diffEditor.dispose()
        // original.dispose()
        // modified.dispose()
        // diffEditorRef.current = null
      }
    }
  }, [diff, diffEditorElementRef])

  useEffect(() => {
    const diffEditorElement = diffEditorElementRef.current

    if (diff != null && diffEditorElement != null && diffEditorRef.current != null) {
      const diffEditor = diffEditorRef.current!
      const containerElement = diffEditorElement!.firstChild as HTMLDivElement
      const modifiedModel = diffEditor.getModifiedEditor().getModel()

      const addCommentId = diffEditor.addCommand(
        0,
        (ctx, selection: monaco.Selection) => {
          const modifiedEditor = diffEditor.getModifiedEditor()
          const startPosition = selection.getStartPosition()
          const endPosition = selection.getEndPosition()
          const range = new monaco.Range(
            startPosition.lineNumber,
            1,
            endPosition.lineNumber,
            Infinity
          )
          const endPositionPlusOne = new monaco.Position(
            endPosition.lineNumber,
            endPosition.column + 1
          )
          const position = modifiedEditor.getScrolledVisiblePosition(endPositionPlusOne)

          if (position) {
            const { top, left } = position
            const container = modifiedEditor.getDomNode()!
            const rootRect = containerElement.getBoundingClientRect()
            const containerRect = container.getBoundingClientRect()

            const baseTop = rootRect.top - containerRect.top
            const baseLeft = rootRect.width - containerRect.width

            const relativeTop = baseTop + top
            const relativeLeft = baseLeft + left

            if (cursorLocatorRef.current) {
              cursorLocatorRef.current.style.top = `${relativeTop}px`
              cursorLocatorRef.current.style.left = `${relativeLeft}px`
            }

            setCommentText('')
            commentModalPayloadRef.current = {
              filePath: diff!.bFilePath,
              fileContent: modifiedEditor.getModel()!.getValueInRange(range),
              startLineNumber: startPosition.lineNumber,
              endLineNumber: endPosition.lineNumber
            }
            onReviewModalOpen()
          }
        },
        ''
      )!

      const editCommentId = diffEditor.addCommand(
        0,
        (ctx, comment: DiffComment) => {
          const modifiedEditor = diffEditor.getModifiedEditor()
          const range = new monaco.Range(
            comment.startLineNumber,
            1,
            comment.endLineNumber,
            Infinity
          )
          const endPositionPlusOne = new monaco.Position(comment.endLineNumber + 1, 1)
          const position = modifiedEditor.getScrolledVisiblePosition(endPositionPlusOne)

          if (position) {
            const { top, left } = position
            const container = modifiedEditor.getDomNode()!
            const rootRect = containerElement.getBoundingClientRect()
            const containerRect = container.getBoundingClientRect()

            const baseTop = rootRect.top - containerRect.top
            const baseLeft = rootRect.width - containerRect.width

            const relativeTop = baseTop + top
            const relativeLeft = baseLeft + left

            if (cursorLocatorRef.current) {
              cursorLocatorRef.current.style.top = `${relativeTop}px`
              cursorLocatorRef.current.style.left = `${relativeLeft}px`
            }

            setCommentText(comment.comment)
            commentModalPayloadRef.current = {
              filePath: diff!.bFilePath,
              fileContent: modifiedEditor.getModel()!.getValueInRange(range),
              startLineNumber: comment.startLineNumber,
              endLineNumber: comment.endLineNumber
            }
            onReviewModalOpen()
          }
        },
        ''
      )!

      const getLineRangeText = (startLineNumber: number, endLineNumber: number): string => {
        return startLineNumber === endLineNumber
          ? `line ${startLineNumber}`
          : `line ${startLineNumber}-${endLineNumber}`
      }

      const truncateText = (text: string, limit: number = 60): string => {
        return text.length > limit ? text.slice(0, limit) + '...' : text
      }

      const highlightCommentLines = (
        comments: DiffComment[],
        diffEditor: monaco.editor.IStandaloneDiffEditor
      ) => {
        const decorations: monaco.editor.IModelDeltaDecoration[] = []

        for (const comment of comments) {
          decorations.push({
            range: new monaco.Range(comment.startLineNumber, 1, comment.endLineNumber, 1),
            options: {
              isWholeLine: true,
              className: 'comment-highlight'
            }
          })
        }

        diffEditor.getModifiedEditor().deltaDecorations([], decorations)
      }

      const updateCodeLenses = (comments: DiffComment[]) => {
        if (codelensDisposable) codelensDisposable.dispose()
        const selections = diffEditor.getModifiedEditor().getSelections() || []

        const lenses: monaco.languages.CodeLens[] = []
        for (const comment of comments) {
          const rangeText = getLineRangeText(comment.startLineNumber, comment.endLineNumber)
          const truncatedComment = truncateText(comment.comment)

          lenses.push({
            range: {
              startLineNumber: comment.startLineNumber,
              startColumn: 1,
              endLineNumber: comment.endLineNumber,
              endColumn: 1
            },
            id: editCommentId,
            command: {
              id: editCommentId,
              title: `${truncatedComment} | Edit comment (${rangeText})`,
              arguments: [comment]
            }
          })
        }

        for (const selection of selections) {
          const { startLineNumber, endLineNumber } = selection
          if (
            !comments.some(
              comment =>
                comment.startLineNumber === startLineNumber &&
                comment.endLineNumber === endLineNumber
            )
          ) {
            const rangeText = getLineRangeText(startLineNumber, endLineNumber)
            lenses.push({
              range: { startLineNumber, startColumn: 1, endLineNumber, endColumn: 1 },
              id: addCommentId,
              command: {
                id: addCommentId,
                title: `Leave a comment (${rangeText})`,
                arguments: [selection] as Array<monaco.Selection | DiffComment>
              }
            })
          }
        }

        codelensDisposable = monaco.languages.registerCodeLensProvider('xml', {
          provideCodeLenses: model => {
            if (model === modifiedModel) {
              return { lenses, dispose() {} }
            }
            return { lenses: [], dispose() {} }
          },
          resolveCodeLens: (_, codeLens) => codeLens
        })
      }

      if (!readOnly) {
        modifiedModel?.onDidChangeContent(() => {
          const newFileContent = modifiedModel.getValue()
          onContentChange({
            filePath: diff?.bFilePath ?? '',
            fileContent: newFileContent
          })
        })

        if (!isCodelensRegistered.current) {
          isCodelensRegistered.current = true
          diffEditor.getModifiedEditor().onDidChangeCursorPosition(() => {
            updateCodeLenses(commentsRef.current)
          })
          globalEventEmitter.on(AppEvents.ReviewCommentsUpdated, comments => {
            updateCodeLenses(commentsRef.current)
            highlightCommentLines(commentsRef.current, diffEditor)
          })
        }
      }

      return () => {
        // diffEditor.dispose()
        // original.dispose()
        // modified.dispose()
      }
    }
  }, [diff, diffEditorElementRef])

  useEffect(() => {
    globalEventEmitter.emit(AppEvents.ReviewCommentsUpdated, comments)
  }, [comments])

  const [commentText, setCommentText] = useState('')
  const uuid = useConversationUuid()

  const onComment = () => {
    if (commentModalPayloadRef.current != null) {
      const comment = Object.assign(commentModalPayloadRef.current, { comment: commentText })
      upsertComment(uuid, comment)
      setCommentText('')
    }
    onReviewModalClose()
  }

  return diff ? (
    // <div className='diff-editor-wrapper w-[calc(100%-130px)] h-full relative' ref={diffEditorElementRef}>
    <div className='diff-editor-wrapper w-full h-full px-8 relative ' ref={diffEditorElementRef}>
      <div className='diff-editor w-full h-full border border-gray-300'></div>
      <Popover
        isOpen={isReviewModalOpen}
        onOpenChange={onReviewModalOpenChange}
        placement='bottom-start'
        className='w-[450px]'
        classNames={{
          content: 'rounded-md shadow-2xl p-4 border-none ring-1 ring-[#E5E7EB]'
        }}
      >
        <PopoverTrigger>
          <div
            className='absolute cursor-locator w-[1px] h-[1px] bg-transparent top-0'
            ref={cursorLocatorRef}
          ></div>
        </PopoverTrigger>
        <PopoverContent>
          <div className='flex flex-col w-full'>
            <SimpleTextarea
              minRows={5}
              placeholder='Write your comment here...'
              autoFocus
              value={commentText}
              onChange={setCommentText}
            />
            <div className='buttons flex justify-end'>
              <SecondaryButton onClick={onReviewModalClose} className='mr-1'>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={onComment} className='ml-1'>
                Comment
              </PrimaryButton>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  ) : (
    <div className='w-full h-full flex items-center justify-center'>
      <div className='flex flex-col items-center text-neutral-400'>
        <VscCode size={100} />
        <div className='loading-shimmer opacity-80'>{t(I18nKey.CODE_EDITOR$LOADING_MESSAGE)}</div>
      </div>
    </div>
  )
}

export default memo(
  DiffEditorComponent,
  (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps)
)
